/**
 * Evidence Ledger — the court-defensible spine of the case as a table.
 * Lists every DETERMINISTIC edge (the chain you can take to a magistrate),
 * grouped by evidence tier, plus the cross-surface handover seams.
 *
 * Demo sandbox (/fusion with Demo data ON) reads the synthetic seed.
 * Real cases (/cases/:id/canvas) and "My data only" read the live Cytoscape
 * graph — never the Rajesh/HDFC demo seed.
 */
import { useEffect, useState } from 'react';
import type cytoscape from 'cytoscape';
import { deterministicChain, detectHandovers } from '../../data/fusionStats';
import { nodeById } from '../../data/fusionSeed';
import { useFusionStore } from '../../stores/useFusionStore';
import { MODULE_COLOR, type FusionModule, type FusionEdge, type Attribution } from '../../types/fusion';

const TIER_NAME = ['Tier 0 · cryptographic', 'Tier 1 · legal / notice-return', 'Tier 2 · deterministic registry', 'Tier 3 · scored'];
const TIER_TINT = ['bg-green-50 border-green-200', 'bg-blue-50 border-blue-200', 'bg-violet-50 border-violet-200', 'bg-amber-50 border-amber-200'];

type LedgerRow = {
  edge: FusionEdge;
  sourceLabel: string;
  targetLabel: string;
  sourceModule?: FusionModule;
  targetModule?: FusionModule;
};

type HandoverRow = {
  edgeId: string;
  from: FusionModule;
  to: FusionModule;
  determinism: 'deterministic' | 'scored';
};

function Dot({ m }: { m?: FusionModule }) {
  return <span className="inline-block w-2 h-2 rounded-full align-middle" style={{ background: m ? MODULE_COLOR[m] : '#94a3b8' }} />;
}

function liveCy(): cytoscape.Core | null {
  const cy = (window as unknown as { __fusionCy?: cytoscape.Core }).__fusionCy;
  if (!cy || (typeof cy.destroyed === 'function' && cy.destroyed())) return null;
  return cy;
}

function tierFromEdge(e: cytoscape.EdgeSingular): Attribution['tier'] {
  const raw = e.data('attribution');
  if (raw && typeof raw === 'object' && typeof (raw as Attribution).tier === 'number') {
    const t = (raw as Attribution).tier;
    if (t === 0 || t === 1 || t === 2 || t === 3) return t;
  }
  // Deterministic case edges without an explicit tier are registry-grade (tier 2).
  return 2;
}

function liveLedgerFromCanvas(): { rows: LedgerRow[]; handovers: HandoverRow[] } {
  const cy = liveCy();
  if (!cy) return { rows: [], handovers: [] };

  const nodeMeta = (id: string) => {
    const n = cy.getElementById(id);
    if (n.empty()) return { label: id, module: undefined as FusionModule | undefined };
    return {
      label: String(n.data('label') || id),
      module: (n.data('module') as FusionModule | undefined) || undefined,
    };
  };

  const rows: LedgerRow[] = [];
  const handovers: HandoverRow[] = [];

  cy.edges().forEach((e) => {
    if (!e.visible()) return;
    const det = String(e.data('determinism') || 'deterministic') as 'deterministic' | 'scored';
    const srcId = String(e.source().id());
    const tgtId = String(e.target().id());
    const src = nodeMeta(srcId);
    const tgt = nodeMeta(tgtId);

    if (src.module && tgt.module && src.module !== tgt.module) {
      handovers.push({
        edgeId: e.id(),
        from: src.module,
        to: tgt.module,
        determinism: det,
      });
    }

    if (det !== 'deterministic') return;

    const attribution: Attribution = {
      method: String(e.data('edge_type') || 'trace'),
      tier: tierFromEdge(e),
      confidence: typeof e.data('confidence') === 'number' ? e.data('confidence') : 1,
      source: 'case_graph',
      evidence_ref: e.data('join_key') ? String(e.data('join_key')) : null,
    };

    rows.push({
      edge: {
        id: e.id(),
        source: srcId,
        target: tgtId,
        edge_type: String(e.data('edge_type') || 'trace'),
        value: typeof e.data('value') === 'number' ? e.data('value') : undefined,
        unit: e.data('unit') ? String(e.data('unit')) : undefined,
        determinism: 'deterministic',
        join_key: e.data('join_key') ? String(e.data('join_key')) : undefined,
        attribution,
      },
      sourceLabel: src.label,
      targetLabel: tgt.label,
      sourceModule: src.module,
      targetModule: tgt.module,
    });
  });

  rows.sort((a, b) => a.edge.attribution.tier - b.edge.attribution.tier);
  return { rows, handovers };
}

function seedLedger(): { rows: LedgerRow[]; handovers: HandoverRow[] } {
  const chain = deterministicChain();
  const handovers = detectHandovers().map((h) => ({
    edgeId: h.edgeId,
    from: h.from,
    to: h.to,
    determinism: h.determinism,
  }));
  const rows: LedgerRow[] = chain.map((e) => {
    const s = nodeById(e.source);
    const t = nodeById(e.target);
    return {
      edge: e,
      sourceLabel: s?.label || e.source,
      targetLabel: t?.label || e.target,
      sourceModule: s?.module,
      targetModule: t?.module,
    };
  });
  return { rows, handovers };
}

export function FusionEvidenceLedger({
  onClose,
  /** seed = /fusion demo; canvas = live Cytoscape on a real case / my-data */
  source = 'seed',
}: {
  onClose?: () => void;
  source?: 'seed' | 'canvas';
}) {
  const select = useFusionStore((s) => s.select);
  const selected = useFusionStore((s) => s.selected);
  const [live, setLive] = useState<{ rows: LedgerRow[]; handovers: HandoverRow[] } | null>(null);

  useEffect(() => {
    if (source !== 'canvas') return;
    const tick = () => setLive(liveLedgerFromCanvas());
    tick();
    const id = window.setInterval(tick, 800);
    return () => window.clearInterval(id);
  }, [source]);

  const ledger = source === 'canvas' ? (live ?? { rows: [], handovers: [] }) : seedLedger();
  const { rows, handovers } = ledger;

  const byTier = [0, 1, 2].map((t) => ({
    tier: t,
    rows: rows.filter((r) => r.edge.attribution.tier === t),
  })).filter((g) => g.rows.length);

  return (
    <div className="flex flex-col h-full text-xs">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-card">
        <div>
          <div className="font-semibold text-sm">Court-safe Chain</div>
          <div className="text-[10px] text-muted-foreground">
            The deterministic, court-defensible chain · {rows.length} links
            {source === 'canvas' ? ' · this case' : ' · demo seed'}
          </div>
        </div>
        {onClose && <button onClick={onClose} className="text-muted-foreground hover:text-foreground px-2 py-1 rounded border border-border">Close</button>}
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {source === 'canvas' && rows.length === 0 && (
          <div className="rounded border border-dashed border-border bg-secondary/30 px-3 py-4 text-muted-foreground">
            <p className="font-medium text-foreground/80">No deterministic links on this case yet</p>
            <p className="mt-1 text-[10px]">
              Only verified joins (UTR, tx hash, registry) appear here. Phone / Instagram / scored OSINT
              leads stay in the Inspector and Journal until they are corroborated.
            </p>
          </div>
        )}

        {byTier.map((g) => (
          <div key={g.tier}>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">{TIER_NAME[g.tier]}</div>
            <div className="space-y-1.5">
              {g.rows.map((r) => {
                const e = r.edge;
                const on = selected?.kind === 'edge' && selected.id === e.id;
                return (
                  <button key={e.id} onClick={() => select({ id: e.id, kind: 'edge' })}
                    className={`w-full text-left rounded border px-2 py-1.5 ${TIER_TINT[g.tier]} ${on ? 'ring-2 ring-sky-400' : ''} hover:brightness-95`}>
                    <div className="flex items-center gap-1.5 font-medium">
                      <Dot m={r.sourceModule} /><span className="truncate">{r.sourceLabel}</span>
                      <span className="text-muted-foreground">→</span>
                      <Dot m={r.targetModule} /><span className="truncate">{r.targetLabel}</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-2">
                      {e.join_key && <span className="font-mono">{e.join_key}</span>}
                      {!!e.value && <span>· {e.value.toLocaleString()} {e.unit}</span>}
                      <span>· {Math.round(e.attribution.confidence * 100)}%</span>
                      {e.attribution.evidence_ref && <span className="text-foreground/70">· {e.attribution.evidence_ref}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Cross-surface handovers · {handovers.length}</div>
          {handovers.length === 0 ? (
            <p className="text-[10px] text-muted-foreground italic">No cross-surface handovers on the current graph.</p>
          ) : (
            <div className="space-y-1">
              {handovers.map((h) => (
                <button key={h.edgeId} onClick={() => select({ id: h.edgeId, kind: 'edge' })}
                  className="w-full text-left flex items-center justify-between gap-2 rounded border border-border px-2 py-1 hover:bg-secondary/60">
                  <span className="flex items-center gap-1.5">
                    <Dot m={h.from} /><span className="capitalize">{h.from}</span>
                    <span className="text-muted-foreground">→</span>
                    <Dot m={h.to} /><span className="capitalize">{h.to}</span>
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${h.determinism === 'deterministic' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800 border border-dashed border-amber-400'}`}>
                    {h.determinism === 'deterministic' ? 'verified' : 'scored'}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <p className="text-[10px] text-muted-foreground italic pt-1 border-t border-border">
          A handover is where one surface hands the trail to the next (bank → off-ramp → on-chain → exchange).
          Verified handovers carry a deterministic join key; scored ones need corroboration before they enter a charge-sheet.
          In a real case these promote into the case <span className="font-medium">Evidence Locker</span> (persisted records + notice lifecycle).
        </p>
      </div>
    </div>
  );
}
